import type { LaunchStats, UpcomingLaunch } from '@vtak/mod-space/client';

/** Milliseconds in seven days, for the "next 7 days" window. */
const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * Headline launch statistics. The year/success tiles are always worldwide
 * (LL2 aggregate counts); the upcoming-derived tiles describe the pool passed
 * in, whose scope is named by the `scope` badge (e.g. "Unit focus").
 */
export function LaunchStatsCard({
  stats,
  upcoming,
  scope,
}: {
  stats: LaunchStats;
  /** The upcoming pool the user is looking at (focused when the focus lens is on). */
  upcoming: UpcomingLaunch[];
  /** Short label naming the scope of the upcoming-derived tiles. */
  scope: string;
}): React.JSX.Element {
  return (
    <div className="rounded-lg border border-echo-border bg-echo-grey/80 p-4">
      <div className="mb-3 flex items-baseline justify-between">
        <div className="font-chakra text-xs font-bold uppercase tracking-[0.2em] text-echo-yellow">Statistics</div>
        <div className="text-[9px] uppercase tracking-wider text-muted-foreground">{scope}</div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Stat label="This Year (Global)" value={stats.totalThisYear} />
        <Stat label="Success Rate (Global)" value={successRate(stats)} />
        <Stat label="Next 7 Days" value={countNext7Days(upcoming)} />
        <Stat label="Upcoming Tracked" value={upcoming.length} />
      </div>
    </div>
  );
}

/** A single big-number stat. */
function Stat({ label, value }: { label: string; value: string | number }): React.JSX.Element {
  return (
    <div className="rounded-md bg-white/[0.03] p-3 text-center">
      <div className="font-mono text-3xl font-bold text-foreground">{value}</div>
      <div className="mt-1 text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
    </div>
  );
}

/** Year-to-date success rate as a percentage string, or "—" when unknown. */
function successRate(stats: LaunchStats): string {
  if (stats.totalThisYear <= 0) return '—';
  return `${Math.round((stats.successesThisYear / stats.totalThisYear) * 100)}%`;
}

/** Count of upcoming launches lifting off within the next seven days. */
function countNext7Days(upcoming: UpcomingLaunch[]): number {
  const now = Date.now();
  const horizon = now + SEVEN_DAYS_MS;
  return upcoming.filter((launch) => {
    const net = new Date(launch.net).getTime();
    return Number.isFinite(net) && net >= now && net <= horizon;
  }).length;
}
